/**
 * HubsTab - Hub ordering tab for NavigationCustomization
 * Allows drag-and-drop reordering of sidebar hubs
 */
import { GripVertical } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { HubDefinition } from '../../lib/hubRegistry'
import type { HubId } from '../types'

interface HubsTabProps {
  orderedHubs: HubDefinition[]
  hubOrder: HubId[]
  currentHub?: HubId
  draggedHub: HubId | null
  dragOverHub: HubId | null
  onDragStart: (hubId: HubId) => void
  onDragOver: (hubId: HubId) => void
  onDrop: (hubId: HubId) => void
  onDragEnd: () => void
}

export function HubsTab({
  orderedHubs,
  currentHub,
  draggedHub,
  dragOverHub,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: HubsTabProps) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        Drag to reorder hubs in your sidebar. This order applies across all Quotech modules.
      </p>
      
      <div className="space-y-2">
        {orderedHubs.map(hub => {
          const HubIcon = hub.icon
          const isDragging = draggedHub === hub.id
          const isDragOver = dragOverHub === hub.id
          const isCurrent = currentHub === hub.id
          
          return (
            <div
              key={hub.id}
              draggable
              onDragStart={() => onDragStart(hub.id)}
              onDragOver={e => { e.preventDefault(); onDragOver(hub.id) }}
              onDrop={() => onDrop(hub.id)}
              onDragEnd={onDragEnd}
              className={cn(
                'flex items-center gap-3 px-4 py-3 rounded-xl border transition-all cursor-grab',
                isDragging && 'opacity-50 scale-95',
                isDragOver && 'border-teal-400 bg-teal-50 dark:bg-teal-900/20',
                !isDragging && !isDragOver && 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-white dark:bg-slate-800'
              )}
            >
              <GripVertical className="w-4 h-4 text-slate-400" />
              
              <div className={cn(
                'w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br',
                hub.accentGradient
              )}>
                <HubIcon className="w-4 h-4 text-white" />
              </div>
              
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  {hub.name}
                </p>
                <p className="text-xs text-slate-400">
                  {hub.pages.length} pages
                </p>
              </div>
              
              {isCurrent && (
                <span className="px-2 py-0.5 text-[10px] font-medium bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 rounded-full">
                  Current
                </span>
              )}
              
              {hub.status === 'development' && (
                <span className="px-2 py-0.5 text-[10px] font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 rounded-full">
                  DEV
                </span>
              )}
              
              {hub.status === 'planned' && (
                <span className="px-2 py-0.5 text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-full">
                  SOON
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
